export const SUPPLIER_CREATE_MULTIPLE_BRANDS_ERROR =
  'Your account has multiple brand admin roles configured. Please contact OMS support so we can correct your account.'

export const SUPPLIER_CREATE_PLATFORM_AND_BRAND_ERROR =
  'Your account has both OMS Admin and brand admin access configured. Please contact OMS support so we can correct your account.'

export const SUPPLIER_CREATE_NO_PERMISSION_ERROR =
  'You do not have permission to create suppliers.'

const countBrandAdminRoles = account =>
  [account?.TFRAdmin, account?.GREENAdmin, account?.JUSTAdmin].filter(Boolean)
    .length

/**
 * Resolve create-time brand context for the admin Create supplier flow.
 * @param {object|null|undefined} account
 * @returns {{
 *   ok: true,
 *   displayTag: 'TFR' | 'GREEN' | 'JUST' | 'OMS Admin',
 *   brandFlags: { TFRSupplier?: boolean, GREENSupplier?: boolean, JUSTSupplier?: boolean },
 * } | {
 *   ok: false,
 *   error: string,
 * }}
 */
export const getSupplierCreateBrandContext = account => {
  if (!account) {
    return { ok: false, error: SUPPLIER_CREATE_NO_PERMISSION_ERROR }
  }

  const brandRoleCount = countBrandAdminRoles(account)
  const hasBrandAdminRole = brandRoleCount > 0
  const isPlatformAdmin = Boolean(account.isAdmin)

  if (brandRoleCount > 1) {
    return { ok: false, error: SUPPLIER_CREATE_MULTIPLE_BRANDS_ERROR }
  }

  if (isPlatformAdmin && hasBrandAdminRole) {
    return { ok: false, error: SUPPLIER_CREATE_PLATFORM_AND_BRAND_ERROR }
  }

  if (account.TFRAdmin) {
    return {
      ok: true,
      displayTag: 'TFR',
      brandFlags: { TFRSupplier: true },
    }
  }

  if (account.GREENAdmin) {
    return {
      ok: true,
      displayTag: 'GREEN',
      brandFlags: { GREENSupplier: true },
    }
  }

  if (account.JUSTAdmin) {
    return {
      ok: true,
      displayTag: 'JUST',
      brandFlags: { JUSTSupplier: true },
    }
  }

  if (isPlatformAdmin) {
    return {
      ok: true,
      displayTag: 'OMS Admin',
      brandFlags: {},
    }
  }

  return { ok: false, error: SUPPLIER_CREATE_NO_PERMISSION_ERROR }
}

/**
 * Merge create-time brand flags onto a supplier create payload.
 * @param {object} supplier
 * @param {object|null|undefined} account
 */
export const applySupplierCreateBrandFlags = (supplier, account) => {
  const context = getSupplierCreateBrandContext(account)

  if (!context.ok) {
    return { ok: false, error: context.error }
  }

  return {
    ok: true,
    payload: { ...supplier, ...context.brandFlags },
    displayTag: context.displayTag,
  }
}

export const getSupplierCreateSuccessMessage = supplier => {
  const supplierName = [supplier?.title, supplier?.secondTitle]
    .filter(Boolean)
    .join(' ')

  return supplierName
    ? `Supplier "${supplierName}" created successfully`
    : 'Supplier created successfully'
}

export const getSupplierCreateBrandTagColor = displayTag => {
  switch (displayTag) {
    case 'TFR':
      return 'blue'
    case 'GREEN':
      return 'green'
    case 'JUST':
      return 'purple'
    case 'OMS Admin':
      return 'gold'
    default:
      return 'default'
  }
}
